import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { DropdownOption } from 'types/ui'
import { loadBusinessUsers } from '@disco/state-management/actions/BusinessUsersActions'
import { getBusinessUsers } from '@disco/state-management/selectors/user'
import { getUserDropdownObject } from '@disco/utils/user'
import UserSelector from 'components/common/user-selector'
import { Box, Flex as DsFlex, RadioGroup, Text } from 'components/ds'
import Button from 'components/ui/button'
import { useDispatch, useSelector } from 'hooks/use-redux'

interface NotificationsTabProps {
  onCancel: () => void
  onSave: (data: NotificationFormData) => void
  emailEnabled: boolean
  notifyUserIds?: number[]
}

export interface NotificationFormData {
  emailEnabled: boolean
  notifyUserIds: number[]
}

const NotificationsTab: React.FC<NotificationsTabProps> = ({
  onCancel,
  onSave,
  emailEnabled: initialEmailEnabled,
  notifyUserIds: initialNotifyUserIds = [],
}) => {
  const dispatch = useDispatch()
  const businessUsers = useSelector(getBusinessUsers)
  const containerRef = useRef<HTMLDivElement>(null)

  const [emailEnabled, setEmailEnabled] = useState(initialEmailEnabled)
  const [sortedUsers, setSortedUsers] = useState([])
  const [chosenUsers, setChosenUsers] = useState<DropdownOption[]>([])
  const [notifyUserIds, setNotifyUserIds] =
    useState<number[]>(initialNotifyUserIds)

  const onUserSelect = useCallback(
    users => {
      const newNotifyUserIds = users.map(({ value }) => value)
      setNotifyUserIds(newNotifyUserIds)
      const newChosenUsers = sortedUsers
        .map(user => {
          if (newNotifyUserIds.includes(user.id)) {
            return getUserDropdownObject(user)
          }
        })
        .filter(Boolean)
      setChosenUsers([...newChosenUsers])
    },
    [sortedUsers],
  )

  const handleSave = useCallback(() => {
    const formData: NotificationFormData = {
      emailEnabled,
      notifyUserIds,
    }
    onSave(formData)
  }, [emailEnabled, notifyUserIds, onSave])

  useEffect(() => {
    if (!businessUsers.loading && !businessUsers.loaded) {
      dispatch(loadBusinessUsers())
    }
  }, [dispatch, businessUsers.loading, businessUsers.loaded])

  useEffect(() => {
    const users = businessUsers.users

    if (users.length && !sortedUsers.length) {
      const normalizedUsers = users.map(u => {
        const { user } = u

        if (user.first_name) {
          return { ...user, last_name: `${user.last_name} (${user.email})` }
        }

        return user
      })
      const chosenUsers = normalizedUsers
        .map(user => {
          if (initialNotifyUserIds.includes(user.id)) {
            return getUserDropdownObject(user)
          }
        })
        .filter(Boolean)
      setChosenUsers(chosenUsers)
      setSortedUsers(normalizedUsers)
    }
  }, [businessUsers.users, sortedUsers.length, initialNotifyUserIds])

  return (
    <DsFlex direction="column" gap="5" ref={containerRef}>
      <Box>
        <Text size="2" weight="medium" mb="3" as="div">
          Choose how you’d like notifications delivered:
        </Text>
        <RadioGroup.Root
          value={emailEnabled?.toString() || 'false'}
          onValueChange={(value: string) => {
            setEmailEnabled(value === 'true')
          }}
        >
          <DsFlex direction="column" gap="3">
            <RadioGroup.Item value="false">
              <Text size="2">In-app notifications</Text>
            </RadioGroup.Item>
            <RadioGroup.Item value="true">
              <Text size="2">In-app and email notifications</Text>
            </RadioGroup.Item>
          </DsFlex>
        </RadioGroup.Root>
      </Box>

      <Box>
        <Text size="2" weight="medium" mb="3" as="div">
          Send notifications of brief submissions to:
        </Text>
        <UserSelector
          users={sortedUsers}
          chosenUsers={chosenUsers}
          usersLoading={businessUsers.loading}
          onUserSelect={onUserSelect}
          scrollContainer={containerRef.current}
        />
      </Box>

      <DsFlex gap="3" mt="2" justify="end">
        <Button appearance="subtle" caption="Cancel" onClick={onCancel} />
        <Button appearance="primary" caption="Save" onClick={handleSave} />
      </DsFlex>
    </DsFlex>
  )
}

export default NotificationsTab
